// System
import { parseISO } from 'date-fns';
import { ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import { Button, Divider, Stack, Typography } from '@mui/material';
// Components
import { AssetIcon } from '@Components/shared/images/AssetIcon';
import { DateLabel } from '@Components/shared/labels/DateLabel';
// Graphql
import { useGetProviderConfigsQuery } from '@Src/generated.graphql';
// Redux
import { useProviderDrawer } from '@Modal/hooks/providers/useProviderDrawer';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
import { useUpdateProviderConfig } from '@Modal/hooks/providers/useUpdateProviderConfig';
import { useCreateProviderConfig } from '@Modal/hooks/providers/useCreateProviderConfig';
// Styles
import Styles from './SetupProviderCurrencies.module.scss';

export const SetupProviderCurrencies: React.FC = () => {
  const { selectedProviderData, uploadCurrencies } = useProviderDrawer();
  const dispatch = useDispatch();
  const { setShowCreateProviderConfigForm } = useCreateProviderConfig();
  const {
    setSelectedProviderConfig,
    setShowUpdateProviderConfigForm,
    setShowDeleteProviderConfigForm,
  } = useUpdateProviderConfig();
  const { userPermissions } = useAuthorization();
  const canManageProviderConfigs = userPermissions?.includes('CAN_MANAGE_PROVIDER_CONFIGS');
  const canViewProviderConfigs = userPermissions?.includes('CAN_VIEW_PROVIDER_CONFIGS');
  const { data, loading } = useGetProviderConfigsQuery({
    variables: { id: selectedProviderData?.id },
  });
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value.toUpperCase());
  };

  const filteredProviderConfigs = data?.providerConfigs?.filter((currencyItem) =>
    currencyItem.currency?.toUpperCase().startsWith(searchQuery)
  );

  const formURL = `${window?.location?.origin}/template.xlsx`.replace('/providers', '');

  const downloadFileAtUrl = (url: string) => {
    const aTag = document.createElement('a');
    aTag.href = url;
    aTag.setAttribute('download', 'template.xlsx');
    document.body.appendChild(aTag);
    aTag.click();
    aTag.remove();
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (file) uploadCurrencies(file);
  };

  return (
    <>
      {loading && <AssetIcon src="rental-loading" className={Styles.LoadingIcon} />}
      {canViewProviderConfigs ? (
        <Stack className={Styles.SetupProviderCurrencies}>
          {canManageProviderConfigs && (
            <Stack direction="row" justifyContent="space-between" alignItems="start">
              <Stack
                direction="column"
                justifyContent="space-between"
                gap={2}
                sx={{ width: '200px' }}
              >
                <Button variant="outlined" onClick={() => downloadFileAtUrl(formURL)}>
                  Download Form
                </Button>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  style={{ cursor: 'pointer' }}
                />
              </Stack>
              <Divider
                orientation="vertical"
                sx={{ width: '1px', height: '70px', backgroundColor: '#212B36' }}
              />
              <Stack gap={1}>
                <Button
                  variant="outlined"
                  onClick={() => dispatch(setShowCreateProviderConfigForm(true))}
                  sx={{ width: '200px' }}
                >
                  Add Manually
                </Button>
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  style={{
                    padding: '8px',
                    border: '1px solid #318688',
                    borderRadius: '8px',
                    outline: 'none',
                    width: '100%',
                  }}
                />
              </Stack>
            </Stack>
          )}
          {!canManageProviderConfigs && (
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                padding: '8px',
                border: '1px solid #318688',
                borderRadius: '8px',
                outline: 'none',
                width: '100%',
              }}
            />
          )}
          <Divider />
          {filteredProviderConfigs &&
            filteredProviderConfigs.map((currencyItem) => {
              const { currency, createdAt, id, updatedAt } = currencyItem;
              const handleUpdateClick = () => {
                dispatch(setSelectedProviderConfig(currencyItem));
                dispatch(setShowUpdateProviderConfigForm(true));
              };

              return (
                <Stack className={Styles.CurrencyContainer} key={id}>
                  <Stack className={Styles.FieldColumnContainer}>
                    <Typography className={Styles.Typography}>Currency</Typography>
                    <Typography className={Styles.Typography}>
                      {currency ? currency : 'No Result'}
                    </Typography>
                  </Stack>
                  <Stack className={Styles.FieldColumnContainer}>
                    <Typography className={Styles.Typography}>Created at</Typography>
                    <Typography className={Styles.Typography}>
                      {createdAt ? (
                        <DateLabel date={new Date(parseISO(createdAt))} format="medium" />
                      ) : (
                        'No Result'
                      )}
                    </Typography>
                  </Stack>
                  <Stack className={Styles.FieldColumnContainer}>
                    <Typography className={Styles.Typography}>Updated at</Typography>
                    <Typography className={Styles.Typography}>
                      {updatedAt ? (
                        <DateLabel date={new Date(parseISO(updatedAt))} format="medium" />
                      ) : (
                        'No Result'
                      )}
                    </Typography>
                  </Stack>
                  <Stack className={Styles.ActionsContainer}>
                    <SettingsIcon
                      sx={{ color: 'grey', cursor: 'pointer' }}
                      onClick={handleUpdateClick}
                    />
                    {canManageProviderConfigs && (
                      <DeleteIcon
                        sx={{ color: 'red', cursor: 'pointer' }}
                        onClick={() => {
                          dispatch(setSelectedProviderConfig(currencyItem));
                          dispatch(setShowDeleteProviderConfigForm(true));
                        }}
                      />
                    )}
                  </Stack>
                </Stack>
              );
            })}
        </Stack>
      ) : (
        <strong>NO PERMISSION</strong>
      )}
    </>
  );
};
